import { IconCollection } from "components/Icon/IconCollection";
import { getNewRecordPath } from "components/Layout/Sidebar/components/CreateNew/CreateNew";
import { ProvisioningStateDot } from "components/ProvisioningStateDot/ProvisioningStateDot";
import RecordLabel from "components/RecordLabel";
import { TrashFilterHeaderAction } from "containers/Filter/components/TrashFilterHeaderAction";
import { getRecordColumnCellStyle } from "components/Table/recordColumnWidths";
import { locationColumn } from "components/Table/recordTableUtils";
import { commonColumns } from "containers/AdvancedSearch/components/advancedFilterColumns";
import { buildRecordListAdvancedSearchPath } from "containers/Dashboard/advancedSearchLink";
import { getInitialQueryWithTableSettings } from "components/Table/tableUtils";
import { getSchemaFormColumns } from "containers/Resources/screens/ResourcesSingle/AttributeValueSchema";
import {
    RecordDescriptionInlineEdit,
    RecordFieldInlineEditPencil,
    isRecordFieldEditable,
} from "containers/Resources/screens/ResourcesSingle/RecordFieldInlineEdit";
import { SubrecordTable } from "containers/Resources/screens/ResourcesSingle/components/SubrecordTable";
import {
    customObjectListKey,
    initialQuery,
} from "containers/Resources/screens/ResourcesSingle/ResourcesSingle";
import { Custom_Object, Object_Template } from "graphql/generated/graphql";
import { History } from "history";
import { useCallback, useMemo } from "react";
import { getRelativeUrl } from "utils/service";

export const CustomObjectList = ({
    resourceData,
    recordId,
    objectTemplateList,
    fetchResourceData,
    updateCustomObjectListQuery,
    history,
}: {
    resourceData: Object_Template;
    recordId: string;
    objectTemplateList: Object_Template[];
    fetchResourceData: () => Promise<any>;
    updateCustomObjectListQuery: (query: any) => void;
    history: History;
}) => {
    const customObjectData = useMemo(
        () =>
            resourceData?.custom_object_list_object_template_id?.custom_object_list?.map(
                (customObject) => ({
                    ...customObject,
                    resource_type: "custom_object",
                })
            ),
        [resourceData]
    );

    const columns = useMemo(() => {
        const currentSchema =
            objectTemplateList.find((objectTemplate) => objectTemplate.id === recordId)?.schema ??
            {};

        return [
            {
                label: "Name",
                key: "name",
                shown: true,
                sortable: true,
                cellStyle: getRecordColumnCellStyle("name"),
                dto: (row, refresh) => {
                    const { name, id, resource_type } = row;
                    return (
                        <div className="inlineEditPopoverCell">
                            <div
                                className="inlineEditPopoverCell__preview"
                                style={{
                                    position: "relative",
                                }}
                            >
                                <ProvisioningStateDot id={id} />

                                <RecordLabel
                                    key={id}
                                    link={getRelativeUrl(resource_type, id)}
                                    name={name}
                                    contentStyle={{
                                        marginLeft: "14px",
                                    }}
                                />
                            </div>
                            {isRecordFieldEditable(row) && (
                                <RecordFieldInlineEditPencil
                                    record={row}
                                    field="name"
                                    value={name}
                                    resourceName="custom_object"
                                    refresh={refresh}
                                />
                            )}
                        </div>
                    );
                },
            },
            // Normal record fields (created/modified by & at, location) — reuse the
            // shared record-table columns so this list matches every other resource list.
            locationColumn(),
            commonColumns.modifiedAt(),
            commonColumns.modifiedBy(),
            commonColumns.createdAt(),
            commonColumns.createdBy(),
            {
                label: "In trash",
                key: "deleted_at",
                shown: false,
                sortable: true,
                dto: ({ deleted_at, id, resource_type }) => {
                    return (
                        <RecordLabel
                            key={id}
                            link={getRelativeUrl(resource_type, id)}
                            icon={deleted_at !== null ? IconCollection.check : undefined}
                        />
                    );
                },
            },
            // Addable (not shown by default, so the default column set is unchanged)
            // Description column with a pop-out markdown editor (scope (ii) slice 2).
            // `description` is a real top-level custom_object column, so it sorts
            // server-side like name.
            {
                label: "Description",
                key: "description",
                shown: false,
                sortable: true,
                cellStyle: getRecordColumnCellStyle("description"),
                dto: (row, refresh) => (
                    <RecordDescriptionInlineEdit
                        record={row}
                        value={row?.description}
                        resourceName="custom_object"
                        refresh={refresh}
                    />
                ),
            },
            // Attribute columns are sortable here: the object_template CO list is
            // server-side paged (updateQuery is wired), and the custom_object_list
            // GraphQL order arg accepts "+value.<attr>"/"-value.<attr>". Passing
            // sortable keys each attribute column as `value.<attr>` so a header
            // click emits the correct order token.
            ...getSchemaFormColumns(currentSchema, { sortable: true, editable: true }),
        ];
    }, [objectTemplateList, recordId]);

    const onAddClick = useCallback(() => {
        history.push(
            getNewRecordPath("custom_object", {
                object_template_id: recordId,
                object_template_name: resourceData?.name,
            })
        );
    }, [history, recordId, resourceData?.name]);

    // Jump to the advanced search prefiltered to this object template's custom objects.
    // object_template_id is a real custom_object column, so it works as a customFilter
    // independent of value.<attr> attribute-value filtering.
    const onOpenAdvancedSearch = useCallback(() => {
        history.push(
            buildRecordListAdvancedSearchPath("custom_object", {
                and: [{ field: "object_template_id", op: "eq", value: recordId }],
            })
        );
    }, [history, recordId]);

    // allow_deleted = "show in trash"; allow_normal = "show non-trashed".
    // Reset to the first page since the row set changes. The mount/dedup guard
    // (only fire on a real toggle change) lives inside TrashFilterHeaderAction.
    const onTrashChange = useCallback(
        ({ includeDeleted, hideDeleted }: { includeDeleted: boolean; hideDeleted: boolean }) => {
            updateCustomObjectListQuery({
                allow_deleted: includeDeleted,
                allow_normal: hideDeleted,
                offset: 0,
            });
        },
        [updateCustomObjectListQuery]
    );

    const headerAction = useMemo(
        () => (
            <TrashFilterHeaderAction
                onTrashChange={onTrashChange}
                onShowAll={onOpenAdvancedSearch}
                showAllTitle="Show all these custom objects in the advanced search"
            />
        ),
        [onOpenAdvancedSearch, onTrashChange]
    );

    const tableOptions = useMemo(
        () => ({
            viewColumns: true,
        }),
        []
    );

    return (
        <SubrecordTable
            key={customObjectListKey}
            data={customObjectData as Custom_Object[]}
            totalItemCount={
                resourceData?.custom_object_list_object_template_id?.custom_object_count || 0
            }
            uniqueKey={customObjectListKey}
            title="Custom objects"
            refreshData={fetchResourceData}
            columns={columns}
            emptyListText="No data to display."
            initialQuery={getInitialQueryWithTableSettings(
                `customObjectList-${recordId}`,
                initialQuery
            )}
            updateQuery={updateCustomObjectListQuery}
            isAddButtonDisabled={!!resourceData?.deleted_at}
            onAddClick={onAddClick}
            headerAction={headerAction}
            tableOptions={tableOptions}
            tableSettingsKey={`customObjectList-${recordId}`}
        />
    );
};
